import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Gift, Copy, Mail, MessageCircle, Send, Check, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { safeStorage } from "@/lib/storage";
import MilestoneLadder from "@/components/MilestoneLadder";

/**
 * ReferralCard — always-visible tile on the Dashboard showing the user's
 * personal invite link + earnings. Clicking any share button uses the native
 * Web Share API on mobile (Safari/Chrome) or falls back to a mailto: / SMS /
 * WhatsApp deep link on desktop.
 */
export default function ReferralCard() {
  const [summary, setSummary] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/referrals/summary");
      setSummary(data);
      // Celebrate newly unlocked milestones the first time the user sees them.
      const seenKey = "referral-milestones-seen";
      const seen = new Set(safeStorage.getJSON(seenKey, []));
      const fresh = (data.badges || []).filter((k) => !seen.has(k));
      if (fresh.length) {
        for (const key of fresh) {
          const tier = (data.milestones || []).find((t) => t.key === key);
          if (!tier) continue;
          toast.success(`🎉 Milestone unlocked: ${tier.label}!`, {
            description: tier.description,
            duration: 6000,
          });
          seen.add(key);
        }
        safeStorage.setJSON(seenKey, Array.from(seen));
      }
    } catch (err) {
      console.warn("[ReferralCard] load failed:", err?.message || err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shareText = summary
    ? `I'm using DS-260USA.com to prepare our family's DS-260 consular cases — it fills the forms from your documents, in your language. Use my link and we both get $50 off: ${summary.share_url}`
    : "";

  const copy = async () => {
    if (!summary) return;
    try {
      await navigator.clipboard.writeText(summary.share_url);
      setCopied(true);
      toast.success("Referral link copied — send it to a family member!");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy — long-press the link to share manually.");
    }
  };

  const share = async () => {
    if (!summary) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "DS-260USA.com — $50 off",
          text: shareText,
          url: summary.share_url,
        });
      } catch (err) {
        // AbortError is normal (user cancelled the share sheet). Only log real errors.
        if (err?.name !== "AbortError") console.warn("[ReferralCard] share failed:", err);
      }
    } else {
      copy();
    }
  };

  if (!summary) return null;

  const dollars = Math.round((summary.available_credit_cents || 0) / 100);
  const perRef = Math.round((summary.credit_per_referral_cents || 5000) / 100);

  return (
    <section
      className="card-surface p-5 sm:p-6 anim-in"
      style={{
        background: "linear-gradient(135deg, var(--ai-bg) 0%, #FFF6EF 100%)",
        borderColor: "var(--ai-border)",
      }}
      data-testid="referral-card"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: "var(--action)" }}
        >
          <Gift className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-display font-bold text-base sm:text-lg" style={{ color: "var(--text-primary)" }}>
              Give ${perRef}, get ${perRef}
            </h3>
            {dollars > 0 && (
              <span
                className="pill pill-ok text-xs"
                data-testid="referral-available-credit"
              >
                <Sparkles className="w-3 h-3" /> ${dollars} credit ready
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
            Refer a family member who's also petitioning. When they make their first payment,
            you both get <b>${perRef} off</b> your next payment.
          </p>

          <div className="flex gap-2 mb-3">
            <input
              readOnly
              value={summary.share_url}
              onFocus={(e) => e.target.select()}
              data-testid="referral-share-url"
              className="field-input text-xs flex-1 font-mono"
              style={{ background: "#fff" }}
              aria-label="Your referral link"
            />
            <button
              type="button"
              onClick={copy}
              data-testid="referral-copy-btn"
              className="btn-primary flex items-center gap-1.5 text-xs px-3 shrink-0"
              aria-label="Copy referral link"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              type="button"
              onClick={share}
              data-testid="referral-share-btn"
              className="btn-ghost text-xs flex items-center gap-1.5"
              style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
            >
              <Send className="w-3.5 h-3.5" /> Share…
            </button>
            <a
              href={`mailto:?subject=${encodeURIComponent("A friend invited you to DS-260USA.com — $50 off")}&body=${encodeURIComponent(shareText)}`}
              data-testid="referral-email-btn"
              className="btn-ghost text-xs flex items-center gap-1.5"
              style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
            >
              <Mail className="w-3.5 h-3.5" /> Email
            </a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="referral-whatsapp-btn"
              className="btn-ghost text-xs flex items-center gap-1.5"
              style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
            >
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </a>
            <a
              href={`sms:?&body=${encodeURIComponent(shareText)}`}
              data-testid="referral-sms-btn"
              className="btn-ghost text-xs flex items-center gap-1.5"
              style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
            >
              <MessageCircle className="w-3.5 h-3.5" /> Text
            </a>
          </div>

          <div className="flex items-center gap-4 text-[11px]" style={{ color: "var(--text-secondary)" }}>
            <span data-testid="referral-invited-count">
              <b style={{ color: "var(--text-primary)" }}>{summary.invited_count}</b> invited
            </span>
            <span>·</span>
            <span data-testid="referral-earned-count">
              <b style={{ color: "var(--text-primary)" }}>{summary.earned_count}</b> paid
            </span>
            <span>·</span>
            <span>
              Your code: <b className="font-mono" style={{ color: "var(--action)" }} data-testid="referral-code">
                {summary.code}
              </b>
            </span>
          </div>
        </div>
      </div>

      {summary.milestones?.length > 0 && (
        <div className="mt-5">
          <MilestoneLadder summary={summary} />
        </div>
      )}
    </section>
  );
}
